import { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import {
  CreateCheckoutRequest,
  CreatePortalRequest,
  GetInvoicesRequest,
} from "@/types/requests";
import {
  CreateCheckoutResponse,
  CreateCustomerSessionResponse,
  CreatePortalResponse,
  GetInvoicesResponse,
  GetProductsResponse,
  GetSubscriptionResponse,
  StripeInvoice,
  StripeProduct,
  StripeSubscription,
} from "@/types/responses";

const useGetInvoices = ({ items_per_page, page }: GetInvoicesRequest) => {
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const client = useAxiosClient("/billing");

  const fetch = useCallback(() => {
    client
      .get<GetInvoicesResponse>("/invoices", {
        params: { items_per_page, page },
      })
      .then((response) => {
        setInvoices(response.data.invoices);
        setCount(response.data.count);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client, items_per_page, page]);

  // Re-enter the loading state during render when the query params change.
  const queryKey = JSON.stringify([items_per_page, page]);
  const [prevQueryKey, setPrevQueryKey] = useState(queryKey);
  if (prevQueryKey !== queryKey) {
    setPrevQueryKey(queryKey);
    setLoading(true);
    setError(null);
  }

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch();
  }, [fetch]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { count, error, invoices, loading, refetch };
};

const useGetProducts = () => {
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const client = useAxiosClient("/billing");

  const fetch = useCallback(() => {
    client
      .get<GetProductsResponse>("/products")
      .then((response) => {
        setProducts(response.data.products);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client]);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch();
  }, [fetch]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { error, loading, products, refetch };
};

const useGetSubscription = () => {
  const [subscription, setSubscription] = useState<StripeSubscription | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const client = useAxiosClient("/billing");

  const fetch = useCallback(() => {
    client
      .get<GetSubscriptionResponse>("/subscription")
      .then((response) => {
        setSubscription(response.data.subscription);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client]);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch();
  }, [fetch]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { error, loading, refetch, subscription };
};

const useCreateCheckout = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = useAxiosClient("/billing");

  const createCheckout = useCallback(
    async (data: CreateCheckoutRequest) => {
      setError(null);
      setLoading(true);
      try {
        const response = await client.post<
          CreateCheckoutResponse,
          AxiosResponse<CreateCheckoutResponse>
        >("/checkout", data);
        return response.data.url;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return { createCheckout, error, loading };
};

const useCreatePortal = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = useAxiosClient("/billing");

  const createPortal = useCallback(
    async (data: CreatePortalRequest) => {
      setError(null);
      setLoading(true);
      try {
        const response = await client.post<
          CreatePortalResponse,
          AxiosResponse<CreatePortalResponse>
        >("/portal", data);
        return response.data.url;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return { createPortal, error, loading };
};

const useCreateCustomerSession = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = useAxiosClient("/billing");

  const createCustomerSession = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await client.post<
        CreateCustomerSessionResponse,
        AxiosResponse<CreateCustomerSessionResponse>
      >("/customer-session", {});
      return response.data.client_secret;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);

  return { createCustomerSession, error, loading };
};

export {
  useCreateCheckout,
  useCreateCustomerSession,
  useCreatePortal,
  useGetInvoices,
  useGetProducts,
  useGetSubscription,
};
